import { isEditableFinancialDraft } from './accounting-draft-eligibility';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { AccountingDocumentAction, AccountingDocumentType } from '../accounting-lifecycle.model';

@Component({
    selector: 'app-accounting-workflow-actions',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, TextareaModule],
    template: `
        <div class="workflow-actions">
            <p-button label="Submit" icon="pi pi-send" severity="secondary" [outlined]="true"
                *ngIf="isActionVisible('submit') && documentStatus === 'DRAFT'" [loading]="isActionLoading('submit')"
                [disabled]="isActionDisabled('submit')" (click)="emitSimple('submit')"></p-button>
            <p-button label="Approve" icon="pi pi-check-circle" severity="success" [outlined]="true"
                *ngIf="isActionVisible('approve') && documentStatus === 'SUBMITTED'" [loading]="isActionLoading('approve')"
                [disabled]="isActionDisabled('approve')" (click)="emitSimple('approve')"></p-button>
            <p-button label="Reject" icon="pi pi-times-circle" severity="danger" [outlined]="true"
                *ngIf="isActionVisible('reject') && documentStatus === 'SUBMITTED'" [disabled]="isActionDisabled()"
                (click)="openReason('reject')"></p-button>
            <p-button label="Post" icon="pi pi-check" severity="success"
                *ngIf="isActionVisible('post') && documentStatus === 'APPROVED' && postingStatus !== 'POSTED'" [loading]="isActionLoading('post')"
                [disabled]="isActionDisabled('post')" (click)="post.emit()"></p-button>
            <p-button label="Hold" icon="pi pi-pause" severity="warn" [outlined]="true"
                *ngIf="isActionVisible('hold') && documentStatus !== 'ON_HOLD' && postingStatus !== 'POSTED' && !isTerminal"
                [disabled]="isActionDisabled()" (click)="openReason('hold')"></p-button>
            <p-button label="Release" icon="pi pi-play" severity="secondary" [outlined]="true"
                *ngIf="isActionVisible('release') && documentStatus === 'ON_HOLD'" [loading]="isActionLoading('release')"
                [disabled]="isActionDisabled('release')" (click)="emitSimple('release')"></p-button>
            <p-button label="Cancel" icon="pi pi-ban" severity="danger" [outlined]="true"
                *ngIf="isActionVisible('cancel') && postingStatus !== 'POSTED' && !isTerminal" [disabled]="isActionDisabled()"
                (click)="openReason('cancel')"></p-button>
            <p-button label="Void" icon="pi pi-times" severity="danger" [outlined]="true"
                *ngIf="isActionVisible('void') && postingStatus === 'POSTED'" [disabled]="isActionDisabled()"
                (click)="openReason('void')"></p-button>
            <p-button label="Reverse" icon="pi pi-undo" severity="danger" [outlined]="true"
                *ngIf="isActionVisible('reverse') && postingStatus === 'POSTED'" [disabled]="isActionDisabled()"
                (click)="openReason('reverse')"></p-button>
        </div>

        <p-dialog [(visible)]="reasonVisible" [modal]="true" [style]="{ width: '32rem' }" [header]="dialogTitle">
            <div class="reason-dialog">
                <p *ngIf="pendingAction === 'void' && voidMessage" class="void-warning">{{ voidMessage }}</p>
                <label for="workflowReason">Reason</label>
                <textarea id="workflowReason" pTextarea rows="4" [(ngModel)]="reasonText"
                    placeholder="Enter the required accounting reason"></textarea>
                <small *ngIf="reasonRequired && !reasonText.trim()">A reason is required for this action.</small>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [outlined]="true" [disabled]="isBusy"
                    (click)="reasonVisible = false"></p-button>
                <p-button label="Confirm" icon="pi pi-check"
                    [loading]="pendingAction ? isActionLoading(pendingAction) : false"
                    [disabled]="(reasonRequired && !reasonText.trim()) || isActionDisabled(pendingAction)"
                    (click)="confirmReason()"></p-button>
            </ng-template>
        </p-dialog>
    `,
    styles: [`
        .workflow-actions {
            display: inline-flex;
            align-items: center;
            flex-wrap: wrap;
            gap: .5rem;
        }

        .reason-dialog {
            display: flex;
            flex-direction: column;
            gap: .5rem;
        }

        .reason-dialog small {
            color: #b91c1c;
        }

        .void-warning {
            margin: 0;
            color: #9a3412;
            font-weight: 600;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountingWorkflowActionsComponent implements OnChanges {
    @Input() documentType?: AccountingDocumentType;
    @Input() postedAt?: Date | string | number | null;
    @Input() documentStatus?: string;
    @Input() postingStatus?: string;
    @Input() loadingAction: AccountingDocumentAction | null = null;
    @Input() disabled = false;
    @Input() allowedActions?: AccountingDocumentAction[];
    @Input() voidMessage?: string;
    @Output() workflow = new EventEmitter<{ action: AccountingDocumentAction; reason?: string }>();
    @Output() post = new EventEmitter<void>();

    reasonVisible = false;
    reasonText = '';
    pendingAction?: AccountingDocumentAction;
    reasonRequired = true;
    private pendingConfirmation = false;

    ngOnChanges(changes: SimpleChanges): void {
        if (this.pendingAction && !this.isActionVisible(this.pendingAction)) {
            this.pendingConfirmation = false;
            this.reasonVisible = false;
            this.reasonText = '';
            this.pendingAction = undefined;
        }
        if (changes['loadingAction'] && !this.loadingAction && this.pendingConfirmation) {
            this.pendingConfirmation = false;
            this.reasonVisible = false;
            this.reasonText = '';
            this.pendingAction = undefined;
        }
    }

    get isBusy(): boolean {
        return this.disabled || !!this.loadingAction;
    }

    get isTerminal(): boolean {
        return ['CANCELLED', 'VOIDED', 'REVERSED', 'CLOSED'].includes((this.documentStatus || '').toUpperCase());
    }

    get dialogTitle(): string {
        return this.pendingAction ? `${this.pendingAction.charAt(0).toUpperCase()}${this.pendingAction.slice(1)} document` : 'Confirm action';
    }

    isActionLoading(action: AccountingDocumentAction): boolean {
        return this.loadingAction === action;
    }

    isActionVisible(action: AccountingDocumentAction): boolean {
        if (action === 'cancel' && !isEditableFinancialDraft(this)
            && !((this.documentType === 'CUSTOMER_CREDIT_NOTE' || this.documentType === 'VENDOR_DEBIT_NOTE')
                && (this.documentStatus === 'SUBMITTED' || this.documentStatus === 'APPROVED') && this.postingStatus !== 'POSTED')) return false;
        if (this.documentType === 'INVOICE' && (action === 'void' || action === 'reverse')) return false;
        if (this.documentType === 'INVOICE' && action !== 'close'
            && (this.postingStatus === 'POSTED' || this.documentStatus === 'POSTED' || this.postedAt != null)) return false;
        return !this.allowedActions || this.allowedActions.includes(action);
    }

    isActionDisabled(action?: AccountingDocumentAction): boolean {
        return this.disabled || (!!this.loadingAction && this.loadingAction !== action);
    }

    emitSimple(action: AccountingDocumentAction): void {
        if (this.isActionDisabled(action) || !this.isActionVisible(action)) {
            return;
        }
        this.workflow.emit({ action });
    }

    openReason(action: AccountingDocumentAction): void {
        if (this.isActionDisabled() || !this.isActionVisible(action)) {
            return;
        }
        this.pendingAction = action;
        this.reasonText = '';
        this.reasonRequired = ['reject', 'hold', 'cancel', 'void', 'reverse'].includes(action);
        this.pendingConfirmation = false;
        this.reasonVisible = true;
    }

    confirmReason(): void {
        if (!this.pendingAction || this.isActionDisabled(this.pendingAction) || !this.isActionVisible(this.pendingAction)) {
            return;
        }
        if (this.reasonRequired && !this.reasonText.trim()) return;
        this.pendingConfirmation = true;
        this.workflow.emit({ action: this.pendingAction, reason: this.reasonText });
    }
}
